import { useState, useEffect } from 'react';
import { Modal, Form, Slider, Input, InputNumber, App, Upload, Button, List, Popconfirm, Space, Select, Radio, Tooltip, Steps, Alert, DatePicker } from 'antd';
import type { UploadProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { Upload as UploadIcon, Download, Trash2, FileText, FileSignature, Eye, Image as ImageIcon, File as FileIcon, ArrowRight, ArrowLeft } from 'lucide-react';
import { taskService } from '@/services/taskService';
import { attachmentService, type Attachment } from '@/services/attachmentService';
import { signingService } from '@/services/signingService';
import { userService } from '@/services/userService';
import { documentService } from '@/services/documentService';
import { PdfFullViewer } from '@/components/signing/PdfFullViewer';
import { PdfSignaturePlacer } from '@/components/signing/PdfSignaturePlacer';
import { OfficeViewer } from '@/components/common/OfficeViewer';  // V1.0.62
import type { SignatureRect } from '@/services/signingService';

// ============================================================
// 1. Cập nhật tiến độ
// ============================================================
export function UpdateProgressModal({ taskId, currentProgress, onClose, onSuccess }: {
  taskId: string; currentProgress: number; onClose: () => void; onSuccess: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    form.setFieldsValue({ progress: currentProgress, note: '' });
  }, [currentProgress, form]);

  const submit = async () => {
    const { progress, note } = await form.validateFields();
    setSaving(true);
    try {
      await taskService.updateProgress(taskId, progress, note);
      message.success(`Đã cập nhật tiến độ ${progress}%`);
      onSuccess();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Cập nhật tiến độ" open onCancel={onClose} onOk={submit}
      confirmLoading={saving} okText="Lưu" cancelText="Hủy">
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="progress" label="Phần trăm hoàn thành"
          rules={[{ required: true, message: 'Bắt buộc chọn tiến độ' }]}>
          <Slider min={0} max={100} step={5}
            marks={{ 0: '0%', 25: '25%', 50: '50%', 75: '75%', 100: '100%' }} />
        </Form.Item>
        <Form.Item name="note" label="Ghi chú (tùy chọn)">
          <Input.TextArea rows={3} placeholder="VD: Đã hoàn thành 3/5 subtask, chờ duyệt thiết kế..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ============================================================
// 2. Đề nghị đóng task (→ PENDING_REVIEW)
// V1.0.30: Nếu root task có KPI plan > 0 → bắt buộc nhập kpiRevenueActual
// ============================================================
export function SubmitForReviewModal({ taskId, task, onClose, onSuccess }: {
  taskId: string;
  /** V1.0.30: pass task object để check rootKpiPlan */
  task?: { rootKpiPlan?: number | null };
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // V1.0.30: nếu root có KPI plan > 0 → bắt buộc nhập actual
  const rootKpiPlan = task?.rootKpiPlan;
  const requireKpiActual = !!(rootKpiPlan && rootKpiPlan > 0);

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      // V1.0.30: dùng endpoint /tasks/{id}/request-close (thay vì updateStatus)
      await taskService.requestClose(taskId, {
        resultNote: values.reason,
        kpiRevenueActual: values.kpiRevenueActual ?? null,
      });
      message.success('Đã gửi yêu cầu duyệt. Người giao việc sẽ xem xét và đóng task.');
      onSuccess();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Đề nghị đóng task" open onCancel={onClose} onOk={submit}
      confirmLoading={saving} okText="Gửi yêu cầu" cancelText="Hủy"
      okButtonProps={{ type: 'primary' }} width={560}>
      <p style={{ color: '#5b6875', fontSize: 13 }}>
        Task sẽ chuyển sang trạng thái <b>Chờ duyệt</b>. Người giao việc sẽ kiểm tra và đóng hoặc trả lại.
      </p>
      {requireKpiActual && (
        <div style={{
          background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6,
          padding: '10px 12px', marginTop: 12, fontSize: 12, color: '#7c3a00',
        }}>
          💰 Task gốc có <b>KPI doanh thu kế hoạch: {(rootKpiPlan ?? 0).toLocaleString('vi-VN')} đ</b>.
          Vui lòng nhập <b>doanh thu thực tế</b> mà task này đóng góp.
        </div>
      )}
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="reason" label="Tóm tắt kết quả / Lý do đề nghị đóng"
          rules={[{ required: true, message: 'Bắt buộc nhập tóm tắt' }]}>
          <Input.TextArea rows={4}
            placeholder="VD: Đã triển khai Wazuh + ELK, cấu hình 8 use cases phát hiện, training xong cho team CNTT." />
        </Form.Item>
        {requireKpiActual && (
          <Form.Item
            name="kpiRevenueActual"
            label="💰 KPI Doanh thu thực tế đạt được (VND)"
            rules={[
              { required: true, message: 'Bắt buộc nhập KPI thực tế' },
              { type: 'number', min: 0, message: 'KPI ≥ 0' },
            ]}
            extra="Có thể bằng 0 nếu task này không phát sinh doanh thu trực tiếp."
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={1000000}
              placeholder="VD: 50,000,000"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={((value: string | undefined) => Number(value?.replace(/\D/g, '') ?? 0)) as any}
              addonAfter="đ"
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

// ============================================================
// 3. Trả lại task (→ IN_PROGRESS để người thực hiện làm tiếp)
// ============================================================
export function SendBackModal({ taskId, onClose, onSuccess }: {
  taskId: string; onClose: () => void; onSuccess: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const { reason } = await form.validateFields();
    setSaving(true);
    try {
      // Trả lại = đưa về IN_PROGRESS để assignee làm tiếp
      await taskService.updateStatus(taskId, 'IN_PROGRESS' as any, reason);
      message.success('Đã trả lại task cho người thực hiện');
      onSuccess();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Trả lại task để làm tiếp" open onCancel={onClose} onOk={submit}
      confirmLoading={saving} okText="Trả lại" cancelText="Hủy"
      okButtonProps={{ danger: true }}>
      <p style={{ color: '#d46b08', fontSize: 13 }}>
        Task sẽ chuyển về trạng thái <b>Đang làm</b> để người thực hiện tiếp tục. Lý do sẽ gửi thông báo tới họ.
      </p>
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="reason" label="Lý do trả lại"
          rules={[{ required: true, message: 'Bắt buộc nhập lý do' }]}>
          <Input.TextArea rows={4}
            placeholder="VD: Cần bổ sung thêm tài liệu quy trình, kết quả chưa đầy đủ..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ============================================================
// 3b. Duyệt task (→ COMPLETED)
// ============================================================
export function ApproveTaskModal({ taskId, onClose, onSuccess }: {
  taskId: string; onClose: () => void; onSuccess: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const { note } = await form.validateFields();
    setSaving(true);
    try {
      await taskService.updateStatus(taskId, 'COMPLETED' as any, note);
      message.success('Đã duyệt và đóng task');
      onSuccess();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Duyệt & đóng task" open onCancel={onClose} onOk={submit}
      confirmLoading={saving} okText="Duyệt & đóng" cancelText="Hủy"
      okButtonProps={{ type: 'primary' }}>
      <p style={{ color: '#389e0d', fontSize: 13 }}>
        ✓ Task sẽ chuyển sang trạng thái <b>Đã hoàn thành</b> và đóng lại. Người thực hiện sẽ nhận thông báo.
      </p>
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="note" label="Ghi chú duyệt (tùy chọn)">
          <Input.TextArea rows={3}
            placeholder="VD: Đã kiểm tra kết quả, đầy đủ, đóng task." />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ============================================================
// 4. Upload + list attachments (inline component)
// ============================================================
export function TaskAttachmentsPanel({ taskId }: { taskId: string }) {
  const { message, modal } = App.useApp();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [signFor, setSignFor] = useState<Attachment | null>(null);
  // V1.0.39: Preview inline PDF + image
  const [previewFor, setPreviewFor] = useState<Attachment | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await attachmentService.list(taskId);
      setAttachments(data);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [taskId]);

  const beforeUpload: UploadProps['beforeUpload'] = async (file) => {
    if (file.size > 50 * 1024 * 1024) {
      message.error('File vượt quá 50MB');
      return Upload.LIST_IGNORE;
    }
    setUploading(true);
    try {
      await attachmentService.upload(taskId, file as File);
      message.success(`Đã tải lên: ${file.name}`);
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setUploading(false); }
    return Upload.LIST_IGNORE; // ngăn antd upload tự động
  };

  const handleDelete = (a: Attachment) => {
    modal.confirm({
      title: `Xóa file "${a.fileName}"?`,
      okText: 'Xóa', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await attachmentService.delete(a.id);
          message.success('Đã xóa');
          load();
        } catch (e: any) {
          message.error(e.response?.data?.message ?? e.message);
        }
      },
    });
  };

  const handleDownload = async (a: Attachment) => {
    try {
      // V1.0.42: Nếu file đã ký → tải bản đã ký mới nhất
      const useSigned = a.signatureCount > 0;
      await attachmentService.download(a.id, a.fileName, useSigned);
      if (useSigned) {
        message.success('Đã tải bản đã ký điện tử mới nhất');
      }
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  // V1.0.39: Detect file có thể xem trực tiếp (PDF + image)
  // V1.0.62: Thêm docx + xlsx + xls + csv (dùng OfficeViewer)
  const canPreviewInline = (a: Attachment): boolean => {
    const mt = a.mimeType ?? '';
    const fn = a.fileName ?? '';
    if (mt.startsWith('image/')) return true;
    if (mt === 'application/pdf') return true;
    // V1.0.62: Word + Excel + CSV
    if (mt.includes('wordprocessingml')) return true;
    if (mt.includes('spreadsheetml') || mt.includes('excel') || mt.includes('csv')) return true;
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg|pdf|docx|xlsx|xls|xlsm|csv)$/i.test(fn);
  };

  // V1.0.42: Detect file là PDF — chỉ PDF mới gửi ký được
  const isPdfFile = (a: Attachment): boolean => {
    const mt = a.mimeType ?? '';
    const fn = a.fileName ?? '';
    return mt === 'application/pdf' || /\.pdf$/i.test(fn);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: '#7d8a98' }}>{attachments.length} file · Tối đa 50MB/file</div>
        <Upload beforeUpload={beforeUpload} showUploadList={false}>
          <Button size="small" icon={<UploadIcon size={12} />} loading={uploading}>
            Tải lên file
          </Button>
        </Upload>
      </div>

      <List
        loading={loading}
        dataSource={attachments}
        locale={{ emptyText: 'Chưa có file đính kèm' }}
        renderItem={(a) => (
          <List.Item
            key={a.id}
            style={{ padding: '8px 12px', borderBottom: '1px solid #3a4050' }}
            actions={[
              ...(canPreviewInline(a) ? [
                <Tooltip key="view-tip" title={a.signatureCount > 0 ? 'Xem bản đã ký số' : 'Xem nội dung'}>
                  <Button type="text" size="small"
                    icon={a.signatureCount > 0 ? <FileSignature size={14} /> : <Eye size={14} />}
                    onClick={() => setPreviewFor(a)}
                    style={{ color: a.signatureCount > 0 ? '#22863a' : '#52c41a', fontWeight: a.signatureCount > 0 ? 600 : 400 }}>
                    {a.signatureCount > 0 ? 'Xem bản đã ký' : 'Xem'}
                  </Button>
                </Tooltip>
              ] : []),
              <Tooltip
                key="sign-tip"
                title={!isPdfFile(a) ? 'Chỉ có thể gửi ký file PDF. Vui lòng chuyển đổi sang PDF trước.' : ''}
              >
                <Button
                  type="text" size="small" icon={<FileSignature size={14} />}
                  onClick={() => setSignFor(a)}
                  disabled={!isPdfFile(a)}
                  style={{ color: isPdfFile(a) ? '#6699CC' : undefined }}
                >
                  Gửi ký
                </Button>
              </Tooltip>,
              <Button key="dl" type="text" size="small" icon={<Download size={14} />}
                onClick={() => handleDownload(a)}>Tải</Button>,
              <Popconfirm key="del" title={a.signatureCount > 0 ? 'File đã ký, không xóa được' : undefined}
                disabled={a.signatureCount > 0}>
                <Button type="text" size="small" danger icon={<Trash2 size={14} />}
                  disabled={a.signatureCount > 0} onClick={() => handleDelete(a)} />
              </Popconfirm>,
            ]}
          >
            <List.Item.Meta
              avatar={
                canPreviewInline(a)
                  ? (a.mimeType?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(a.fileName))
                    ? <ImageIcon size={20} style={{ color: '#52c41a' }} />
                    : <FileText size={20} style={{ color: '#f5222d' }} />
                  : <FileIcon size={20} style={{ color: '#a5b0c0' }} />
              }
              title={<span style={{ fontSize: 13 }}>{a.fileName}</span>}
              description={
                <Space size={8} style={{ fontSize: 11, color: '#7d8a9e' }}>
                  <span>{formatSize(a.fileSize)}</span>
                  <span>·</span>
                  <span>{a.uploadedByName ?? '?'}</span>
                  <span>·</span>
                  <span>{new Date(a.uploadedAt).toLocaleString('vi-VN')}</span>
                  {a.signatureCount > 0 && <><span>·</span><span style={{ color: '#6699CC' }}>🔏 Đã ký {a.signatureCount} lần</span></>}
                </Space>
              }
            />
          </List.Item>
        )}
      />

      {signFor && (
        <SendSignModal
          attachment={signFor}
          taskId={taskId}
          onClose={() => setSignFor(null)}
          onSuccess={() => { setSignFor(null); load(); }}
        />
      )}

      {previewFor && (
        <TaskAttachmentPreviewModal
          attachment={previewFor}
          onClose={() => setPreviewFor(null)}
          onDownload={() => handleDownload(previewFor)}
        />
      )}
    </div>
  );
}

// ============================================================
// V1.0.39: TaskAttachmentPreviewModal — Inline preview PDF + image
// Vì API attachment cần JWT auth → fetch qua axios + blob URL
// ============================================================
function TaskAttachmentPreviewModal({ attachment, onClose, onDownload }: {
  attachment: Attachment;
  onClose: () => void;
  onDownload: () => void;
}) {
  const { message } = App.useApp();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const mt = attachment.mimeType ?? '';
  const fn = attachment.fileName ?? '';
  const isImage = mt.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(fn);
  const isPdf = mt === 'application/pdf' || /\.pdf$/i.test(fn);
  // V1.0.62: Detect docx + xlsx
  const isDocx = mt.includes('wordprocessingml') || /\.docx$/i.test(fn);
  const isXlsx = mt.includes('spreadsheetml') || mt.includes('excel') || mt.includes('csv')
    || /\.(xlsx|xls|xlsm|csv)$/i.test(fn);
  const isOffice = isDocx || isXlsx;

  // V1.0.42: Nếu file đã ký + PDF → load bản đã ký mới nhất
  const isSigned = attachment.signatureCount > 0 && isPdf;

  useEffect(() => {
    // V1.0.62: Office file (docx/xlsx) sẽ tự load qua OfficeViewer.loadBlob → skip ở đây
    if (isOffice) {
      setLoading(false);
      return;
    }

    let url: string | null = null;
    let cancelled = false;
    setLoading(true);

    attachmentService.previewBlob(attachment.id, attachment.mimeType, isSigned)
      .then(u => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        url = u;
        setBlobUrl(u);
      })
      .catch((e: any) => {
        if (!cancelled) message.error(e.response?.data?.message ?? 'Không tải được file');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment.id]);

  return (
    <Modal
      open
      onCancel={onClose}
      width={isPdf || isOffice ? '90vw' : 'auto'}
      style={{ top: 20, maxWidth: (isPdf || isOffice) ? 1200 : '90vw' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isImage ? <ImageIcon size={16} style={{ color: '#52c41a' }} /> : <FileText size={16} style={{ color: '#f5222d' }} />}
          <span style={{ fontSize: 14 }}>{attachment.fileName}</span>
          {isSigned && (
            <span style={{
              marginLeft: 4, padding: '2px 8px', background: '#e6f7e9',
              color: '#22863a', borderRadius: 3, fontSize: 11, fontWeight: 600,
            }}>
              🔏 BẢN ĐÃ KÝ ({attachment.signatureCount})
            </span>
          )}
        </div>
      }
      footer={
        <Button icon={<Download size={14} />} onClick={onDownload}>
          Tải xuống {isSigned ? 'bản đã ký' : ''}
        </Button>
      }
      destroyOnClose
    >
      {isOffice ? (
        // V1.0.62: Word + Excel viewer
        // V1.0.63i: Nếu file đã được ký số (signatureCount>0) → load bản đã ký mới nhất
        // V1.0.64: Tăng height 78vh → 85vh để viewer chiếm gần full modal
        <div style={{ height: '85vh' }}>
          <OfficeViewer
            loadBlob={() => attachmentService.fetchBlob(attachment.id, attachment.signatureCount > 0)}
            fileName={attachment.fileName}
            fileMime={attachment.mimeType ?? undefined}
            height="100%"
          />
        </div>
      ) : loading || !blobUrl ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#7d8a98' }}>Đang tải file...</div>
      ) : isImage ? (
        <div style={{ textAlign: 'center', maxHeight: '78vh', overflow: 'auto' }}>
          <img
            src={blobUrl}
            alt={attachment.fileName}
            style={{ maxWidth: '100%', maxHeight: '76vh', objectFit: 'contain' }}
          />
        </div>
      ) : isPdf ? (
        // V1.0.47: PdfFullViewer (react-pdf) thay iframe — iOS/Safari mobile xem được mọi trang
        <div style={{ height: '78vh' }}>
          <PdfFullViewer file={blobUrl} documentName={attachment.fileName} height="100%" />
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 40, color: '#7d8a98' }}>
          File này không hỗ trợ xem trực tiếp. Vui lòng tải xuống để xem.
        </div>
      )}
    </Modal>
  );
}

// ============================================================
// 5. Gửi ký - chọn signers + workflow + vị trí ký
// V1.0.63i: Thêm step chọn vị trí ký trên PDF (tùy chọn)
// ============================================================
function SendSignModal({ attachment, taskId, onClose, onSuccess }: {
  attachment: Attachment; taskId: string; onClose: () => void; onSuccess: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  // V1.0.63i: Steps + position
  const [currentStep, setCurrentStep] = useState(0);
  const [signerIds, setSignerIds] = useState<string[]>([]);
  const [workflow, setWorkflow] = useState<'SEQUENTIAL' | 'PARALLEL'>('SEQUENTIAL');
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  // V1.0.63i: Multi-position - 1 user có thể nhiều vị trí ký
  const [positionList, setPositionList] = useState<Array<{ id: string; signerId: string; rect: SignatureRect }>>([]);
  const [selectedSignerId, setSelectedSignerId] = useState<string>('');
  // V1.0.63i: Thông tin yêu cầu ký
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    (async () => {
      setLoadingUsers(true);
      try {
        const { items } = await userService.list({ pageSize: 1000 });
        setUsers(items);
      } catch (e: any) {
        message.error('Không tải được danh sách người dùng');
      } finally { setLoadingUsers(false); }
    })();
    // V1.0.63i: Load loại tài liệu để chọn
    (async () => {
      setLoadingCategories(true);
      try {
        const cats = await documentService.listCategories();
        setCategories(cats.map((c: any) => ({ id: c.id, name: c.name })));
      } catch {
        // tolerate
      } finally { setLoadingCategories(false); }
    })();
    form.setFieldsValue({ workflow: 'SEQUENTIAL' });
  }, []);

  // V1.0.63i: Load PDF blob khi vào step 2
  useEffect(() => {
    if (currentStep !== 1 || pdfBlobUrl) return;
    let url: string | null = null;
    let cancelled = false;
    attachmentService.previewBlob(attachment.id, 'application/pdf', false)
      .then(u => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        url = u;
        setPdfBlobUrl(u);
      })
      .catch(() => message.error('Không tải được file PDF'));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Cleanup blob URL khi unmount
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  const selectedSigners = users
    .filter(u => signerIds.includes(u.id))
    .map(u => ({ id: u.id, fullName: u.fullName, position: u.position, departmentName: u.department?.name }));

  // Step 0 → 1: validate signers
  const goToPositionStep = async () => {
    const values = await form.validateFields();
    if (!values.signerIds || values.signerIds.length === 0) {
      message.warning('Chọn ít nhất 1 người ký');
      return;
    }
    setSignerIds(values.signerIds);
    setWorkflow(values.workflow);
    setSelectedSignerId(values.signerIds[0]);
    setCurrentStep(1);
  };

  // Step 1 → submit
  const submit = async () => {
    if (signerIds.length === 0) {
      message.warning('Chưa chọn người ký');
      return;
    }
    // V1.0.63i: Lấy thêm 3 trường thông tin
    const values = form.getFieldsValue();
    setSaving(true);
    try {
      await signingService.create({
        attachmentId: attachment.id,
        signerIds,
        workflow,
        taskId,
        // V1.0.63i: Thông tin yêu cầu ký
        taskDescription: values.taskDescription,
        categoryId: values.categoryId,
        dueDate: values.dueDate ? (values.dueDate as Dayjs).toISOString() : undefined,
        // V1.0.63i: Multi-position - mỗi item là 1 vị trí, 1 user có thể nhiều vị trí
        signerPositionList: positionList.length > 0
          ? positionList.map(p => ({ signerId: p.signerId, rect: p.rect }))
          : undefined,
      });
      const placedCount = positionList.length;
      message.success(
        placedCount > 0
          ? `Đã gửi yêu cầu ký tới ${signerIds.length} người (${placedCount} vị trí đã định sẵn)`
          : `Đã gửi yêu cầu ký tới ${signerIds.length} người`
      );
      onSuccess();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal
      title={
        <span>
          <FileSignature size={14} style={{ verticalAlign: -2, marginRight: 8, color: '#6699CC' }} />
          Gửi yêu cầu ký: {attachment.fileName}
        </span>
      }
      open onCancel={onClose}
      width={currentStep === 0 ? 580 : '90vw'}
      style={{ top: currentStep === 0 ? undefined : 20, maxWidth: 1200 }}
      footer={
        currentStep === 0 ? (
          <Space>
            <Button onClick={onClose}>Hủy</Button>
            <Button type="primary" onClick={goToPositionStep} icon={<ArrowRight size={14} />}>
              Tiếp tục
            </Button>
          </Space>
        ) : (
          <Space>
            <Button icon={<ArrowLeft size={14} />} onClick={() => setCurrentStep(0)}>
              Quay lại
            </Button>
            <Button onClick={() => setPositionList([])} disabled={positionList.length === 0}>
              Xóa vị trí
            </Button>
            <Button type="primary" onClick={submit} loading={saving} icon={<FileSignature size={14} />}>
              Gửi yêu cầu ký
            </Button>
          </Space>
        )
      }
      destroyOnClose
    >
      <Steps
        current={currentStep}
        size="small"
        style={{ marginBottom: 16 }}
        items={[
          { title: 'Chọn người ký', description: 'Người ký + luồng' },
          { title: 'Vị trí ký', description: 'Tùy chọn — kéo thả' },
        ]}
      />

      {currentStep === 0 ? (
        <Form form={form} layout="vertical">
          <Form.Item name="signerIds" label="Người ký"
            rules={[{ required: true, message: 'Chọn ít nhất 1 người ký' }]}>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              loading={loadingUsers}
              placeholder="Chọn người ký..."
              options={users.map(u => ({
                value: u.id,
                label: `${u.fullName} — ${u.position ?? ''} (${u.department?.name ?? ''})`,
              }))}
            />
          </Form.Item>

          <Form.Item name="workflow" label="Luồng ký"
            rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="SEQUENTIAL">Tuần tự (theo thứ tự)</Radio.Button>
              <Radio.Button value="PARALLEL">Song song (ai cũng ký được)</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* V1.0.63i: Loại tài liệu cần ký (bắt buộc - để lưu kho EDM sau khi ký xong) */}
          <Form.Item name="categoryId" label="Loại tài liệu"
            rules={[{ required: true, message: 'Vui lòng chọn loại tài liệu' }]}
            extra={<div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
              Tài liệu sẽ được lưu vào kho theo loại đã chọn sau khi ký xong.
            </div>}>
            <Select
              showSearch
              optionFilterProp="label"
              loading={loadingCategories}
              placeholder="Chọn loại tài liệu..."
              options={categories.map(c => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>

          {/* V1.0.63i: Mô tả loại tài liệu (bắt buộc) */}
          <Form.Item name="taskDescription" label="Mô tả loại tài liệu cần ký"
            rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
            extra={<div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
              VD: "Hợp đồng mua máy chủ Q2/2026 với nhà cung cấp FPT".
            </div>}>
            <Input.TextArea
              rows={2}
              placeholder="Nhập mô tả chi tiết..."
              maxLength={1000}
              showCount
            />
          </Form.Item>

          {/* V1.0.63i: Hạn ký (tùy chọn) */}
          <Form.Item name="dueDate" label="Thời hạn ký (tùy chọn)">
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder="Chọn ngày hết hạn..."
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>

          <div style={{ fontSize: 11, color: '#a5b0c0', background: '#2d323e', padding: 10, borderRadius: 6 }}>
            <div><b>Tuần tự:</b> Người thứ 2 chỉ ký được sau khi người 1 ký xong.</div>
            <div><b>Song song:</b> Bất kỳ ai cũng có thể ký trước, không cần đợi.</div>
          </div>
        </Form>
      ) : (
        // V1.0.63i: Step 2 - Vị trí ký
        <div>
          <Alert
            message="Chọn vị trí ký (tùy chọn)"
            description={<>
              Chọn người ký bên dưới, sau đó <b>click vào vị trí muốn đặt chữ ký trên PDF</b>.
              Nếu bỏ qua, chữ ký sẽ tự động được đặt ở góc dưới phải trang cuối.
            </>}
            type="info" showIcon style={{ marginBottom: 12 }}
          />
          {pdfBlobUrl ? (
            <div style={{ height: '65vh' }}>
              <PdfSignaturePlacer
                file={pdfBlobUrl}
                signers={selectedSigners}
                positionList={positionList}
                onAddPosition={(signerId, rect) => {
                  setPositionList(prev => [
                    ...prev,
                    { id: `${signerId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, signerId, rect },
                  ]);
                }}
                onRemovePosition={(positionId) => {
                  setPositionList(prev => prev.filter(p => p.id !== positionId));
                }}
                selectedSignerId={selectedSignerId}
                onSelectSigner={setSelectedSignerId}
              />
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 80, color: '#7d8a98' }}>Đang tải PDF...</div>
          )}
        </div>
      )}
    </Modal>
  );
}
